<script>
    import opsChangeInboxService from '@/services/opsChangeInbox.service';
    import Spinner from '@/components/shared/Spinner';

    export default {
        components: { Spinner },
        data() {
            return {
                thread: null,
                isLoading: true,
                error: null
            };
        },
        computed: {
            threadId() {
                return this.$route.params.threadId;
            }
        },
        watch: {
            threadId: {
                immediate: true,
                handler() {
                    this.load();
                }
            }
        },
        methods: {
            apiError(err, fallback) {
                return err?.data?.message || err?.message || fallback;
            },
            formatLocal(value) {
                if (!value) return '—';
                const d = new Date(value);
                if (Number.isNaN(d.getTime())) return value;
                return d.toLocaleString();
            },
            back() {
                this.$router.push('/ops-change-inbox');
            },
            async load() {
                this.isLoading = true;
                this.error = null;
                try {
                    this.thread = await opsChangeInboxService.getThread(this.threadId);
                } catch (err) {
                    this.thread = null;
                    this.error = this.apiError(err, 'Could not load this thread.');
                } finally {
                    this.isLoading = false;
                }
            }
        }
    };
</script>

<template>
    <div class="container-fluid py-3">
        <button class="btn btn-link px-0 mb-2" type="button" @click="back">Back to Setup inbox</button>

        <Spinner v-if="isLoading" />

        <div v-else-if="error" class="alert alert-danger d-flex align-items-center gap-3" role="alert">
            <span class="flex-grow-1">{{ error }}</span>
            <button class="btn btn-sm btn-outline-danger flex-shrink-0" type="button" @click="load">
                Try again
            </button>
        </div>

        <template v-else-if="thread">
            <div class="mb-3">
                <h1 class="h3 mb-1">{{ thread.subject || thread.threadId }}</h1>
                <p class="text-muted mb-1">
                    {{ thread.companyName || thread.companyId || 'Company not identified yet' }}
                    · {{ thread.requesterEmail }}
                </p>
                <p class="mb-0">
                    <span class="badge" :class="thread.closed ? 'text-bg-secondary' : 'text-bg-primary'">
                        {{ thread.statusLabel || (thread.closed ? 'Closed' : 'Open') }}
                    </span>
                    <span v-if="thread.applied" class="badge text-bg-success ms-1">Applied</span>
                    <span class="text-muted ms-2 small">
                        Started {{ formatLocal(thread.startedUtc) }} · Last message
                        {{ formatLocal(thread.lastMessageUtc) }} · your local timezone
                    </span>
                </p>
            </div>

            <div v-if="!(thread.messages && thread.messages.length)" class="text-muted">
                No messages stored for this thread. That is unusual — the first email is normally saved before
                anything replies to it.
            </div>

            <div
                v-for="msg in thread.messages"
                :key="msg.id"
                class="border rounded p-3 mb-3"
                :class="msg.direction === 'outbound' ? 'bg-light' : 'bg-white'"
            >
                <div class="d-flex flex-wrap justify-content-between gap-2 mb-2">
                    <div>
                        <span class="badge me-2" :class="msg.direction === 'outbound' ? 'text-bg-info' : 'text-bg-dark'">
                            {{ msg.direction === 'outbound' ? 'We sent' : 'They sent' }}
                        </span>
                        <strong>{{ msg.fromEmail }}</strong>
                        <span v-if="msg.toEmail" class="text-muted"> to {{ msg.toEmail }}</span>
                    </div>
                    <div class="text-muted small">{{ formatLocal(msg.createdUtc) }}</div>
                </div>
                <div v-if="msg.subject" class="small text-muted mb-2">{{ msg.subject }}</div>
                <pre class="mb-0 thread-body">{{ msg.body || 'This email arrived with no body.' }}</pre>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.thread-body {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 0.9rem;
    line-height: 1.45;
    margin: 0;
}
</style>
